/**
 * SFTP Site Service
 *
 * Handles SFTP site management operations with real API integration.
 * Metadata stored in DynamoDB, credentials stored in SSM Parameter Store.
 */

import apiClient from './api';

/**
 * Get list of SFTP sites with search and pagination
 */
export const getSftpSites = async (params = {}) => {
  try {
    const { search = '', page = 1, limit = 10 } = params;

    const response = await apiClient.get('/sftp-sites', {
      params: { search, page, page_size: limit }
    });

    const apiData = response.data?.data || response.data;
    const siteList = apiData?.sftp_sites || [];
    const sites = siteList.map(site => ({
      id: site.ID,
      name: site.NAME || site.SITE_NAME,
      description: site.DESCRIPTION || '',
      awsSecretName: site.AWS_SECRET_NAME || '',
      isActive: site.IS_ACTIVE !== undefined ? site.IS_ACTIVE : true,
      reportCount: site.REPORT_COUNT || 0,
      createdDate: (site.CREATED_AT || '')?.split('T')[0] || ''
    }));

    return {
      data: sites,
      pagination: {
        page: apiData?.pagination?.page || page,
        limit: apiData?.pagination?.page_size || limit,
        total: apiData?.pagination?.total || 0,
        totalPages: apiData?.pagination?.total_pages || 1
      }
    };
  } catch (error) {
    console.error('Failed to fetch SFTP sites:', error);
    throw error;
  }
};

/**
 * Get all SFTP sites (no pagination, for dropdowns)
 */
export const getAllSftpSites = async () => {
  try {
    const response = await apiClient.get('/sftp-sites', {
      params: { page: 1, page_size: 100 }
    });

    const apiData = response.data?.data || response.data;
    const siteList = apiData?.sftp_sites || [];
    return siteList.map(site => ({
      id: site.ID,
      name: site.NAME || site.SITE_NAME,
      description: site.DESCRIPTION || '',
      isActive: site.IS_ACTIVE !== undefined ? site.IS_ACTIVE : true
    }));
  } catch (error) {
    console.error('Failed to fetch all SFTP sites:', error);
    throw error;
  }
};

/**
 * Get SFTP site by ID (includes non-sensitive connection details)
 */
export const getSftpSiteById = async (siteId) => {
  try {
    const response = await apiClient.get(`/sftp-sites/${siteId}`);
    const apiData = response.data?.data || response.data;
    const conn = apiData.CONNECTION || {};

    return {
      id: apiData.ID,
      name: apiData.NAME || apiData.SITE_NAME,
      description: apiData.DESCRIPTION || '',
      awsSecretName: apiData.AWS_SECRET_NAME || '',
      isActive: apiData.IS_ACTIVE !== undefined ? apiData.IS_ACTIVE : true,
      reportCount: apiData.REPORT_COUNT || 0,
      createdDate: (apiData.CREATED_AT || '')?.split('T')[0] || '',
      connection: {
        host: conn.host || '',
        port: conn.port || 22,
        username: conn.username || '',
        remotePath: conn.remote_path || '',
        authMethod: conn.auth_method || 'password',
        hasCredentials: conn.has_credentials || false,
        hasPassphrase: conn.has_passphrase || false,
        hostKeyFingerprint: conn.host_key_fingerprint || '',
        timeout: conn.timeout || 30,
        notes: conn.notes || ''
      }
    };
  } catch (error) {
    console.error('Failed to fetch SFTP site:', error);
    throw error;
  }
};

/**
 * Create new SFTP site with credentials (admin only)
 */
export const createSftpSite = async (siteData) => {
  try {
    const payload = {
      name: siteData.name,
      description: siteData.description || '',
      host: siteData.host,
      port: parseInt(siteData.port, 10) || 22,
      username: siteData.username,
      auth_method: siteData.authMethod,
      remote_path: siteData.remotePath || '',
      host_key_fingerprint: siteData.hostKeyFingerprint || '',
      timeout: parseInt(siteData.timeout, 10) || 30,
      notes: siteData.notes || ''
    };

    if (siteData.authMethod === 'password') {
      payload.password = siteData.password;
    } else if (siteData.authMethod === 'private_key') {
      payload.private_key = siteData.privateKey;
    } else if (siteData.authMethod === 'private_key_passphrase') {
      payload.private_key = siteData.privateKey;
      payload.passphrase = siteData.passphrase;
    }

    const config = {};
    if (siteData.teamId) {
      config.headers = { 'X-Active-Team-Id': siteData.teamId };
    }

    const response = await apiClient.post('/sftp-sites', payload, config);
    const apiData = response.data?.data || response.data;

    return {
      data: {
        id: apiData.id,
        name: apiData.name,
        awsSecretName: apiData.aws_secret_name || ''
      }
    };
  } catch (error) {
    console.error('Failed to create SFTP site:', error);
    throw error;
  }
};

/**
 * Update SFTP site (admin only). Only sends fields that changed.
 */
export const updateSftpSite = async (siteId, siteData) => {
  try {
    const payload = {};

    // Metadata fields
    if (siteData.name !== undefined) payload.name = siteData.name;
    if (siteData.description !== undefined) payload.description = siteData.description;
    if (siteData.isActive !== undefined) payload.is_active = siteData.isActive;

    // Connection fields
    if (siteData.host) payload.host = siteData.host;
    if (siteData.port) payload.port = parseInt(siteData.port, 10);
    if (siteData.username) payload.username = siteData.username;
    if (siteData.remotePath !== undefined) payload.remote_path = siteData.remotePath;
    if (siteData.authMethod) payload.auth_method = siteData.authMethod;
    if (siteData.hostKeyFingerprint !== undefined) payload.host_key_fingerprint = siteData.hostKeyFingerprint;
    if (siteData.timeout) payload.timeout = parseInt(siteData.timeout, 10);
    if (siteData.notes !== undefined) payload.notes = siteData.notes;

    // Credentials — only if provided (write-only)
    if (siteData.password) payload.password = siteData.password;
    if (siteData.privateKey) payload.private_key = siteData.privateKey;
    if (siteData.passphrase) payload.passphrase = siteData.passphrase;

    await apiClient.put(`/sftp-sites/${siteId}`, payload);
    return { data: { id: siteId } };
  } catch (error) {
    console.error('Failed to update SFTP site:', error);
    throw error;
  }
};

/**
 * Delete SFTP site (admin only, fails if in use by reports)
 */
export const deleteSftpSite = async (siteId) => {
  try {
    await apiClient.delete(`/sftp-sites/${siteId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete SFTP site:', error);
    throw error;
  }
};

/**
 * Browse files on a remote SFTP site
 */
export const browseSftpFiles = async (siteId, remotePath) => {
  try {
    const params = {};
    if (remotePath) params.path = remotePath;
    const response = await apiClient.get(`/sftp-sites/${siteId}/files`, { params });
    const apiData = response.data?.data || response.data;
    return {
      siteName: apiData.site_name || '',
      path: apiData.path || '',
      files: (apiData.files || []).map(f => ({
        name: f.name,
        size: f.size || 0,
        modified: f.modified || null,
        isDirectory: f.is_directory || false
      })),
      total: apiData.total || 0
    };
  } catch (error) {
    console.error('Failed to browse SFTP files:', error);
    throw error;
  }
};
